import React, { Fragment, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import loadable from '@loadable/component';
import { useSearchParams } from 'react-router-dom';
import ClientHydrator, { ServerHydrator } from '../../../../common/Hydrator';
import { getMMT_HLD_OBJ } from '../../../../../../common/utils/appUtils';
import PackageCardLoading from './PackageCardLoading';
// import PackageCard from './PackageCard';
import AppliedFilters from './AppliedFilters';
import ListingSection from './ListingSection';
import ListingSorter from './ListingSorter';
import { ERROR_TYPES } from '../../../../../constants/errorConstants';
import { CUSTOM_SECTIONS, VALID_SECTION_CODES } from '../../../../../constants/sectionConstants';
import ScrollTracker from './ScrollTracker';
import OmnitureUtils from '../../../../../../common/utils/Omniture/OmnitureUtils';
import { getAllAppliedFilters } from '../../../../../helpers/utils';
import './styles.scss';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import './PackageCard/styles.scss';
import './PackageCard/ImageCard/styles.scss';
import './PackageCard/ImageCard/ImageSlider/styles.scss';
import './PackageCard/PackageBottomCard/styles.scss';
import './PackageCard/PackageBottomCard/PricingCard/styles.scss';
import './PackageCard/PackageTopCard/styles.scss';
import './PackageCard/PackageTopCard/ItineraryList/styles.scss';
import './PackageCard/PackageTopCard/Inclusions/styles.scss';
import './PackageCard/PackageTopCard/Highlights/styles.scss';
import { faOnPageExit } from './PackageCard/analytics-services';
import { removeLocalStorage } from '../../../../../../common/utils/browserUtils';

const PackageCard = loadable(
  () => /* webpackChunkName: 'listing-package-card' */ import('./PackageCard')
);

const ErrorPage = loadable(() => /* webpackChunkName: 'listing-error' */ import('./ErrorPage'));

let Hydrator = ClientHydrator;

if (typeof window === 'undefined') {
  Hydrator = ServerHydrator;
}

const PackageDetails = ({
  isLoading,
  packages = [],
  activeTab,
  nextOffset,
  noMorePackages,
  loadMorePackages,
  listingFilters,
  handleFilterChange,
  handleClearAllFilters,
  sectionsByOrderObj,
  packagesCount,
  groupList,
  groupMetaIsLoading,
  tabs,
  sectionsLoading,
  isSectionsLoading,
  metadataIsLoading,
  pageLoading,
  pageLoadingAction,
  loadEventTriggered,
  noPackageError,
}) => {
  const { fromSEO, showPDO } = getMMT_HLD_OBJ();
  const root = useRef(null);
  const observerRef = useRef(null);
  const [search] = useSearchParams();
  let usedSectionsCount = 0;
  let rowCount = 0;
  let batchedPackagesTotalCount = 0;
  let actualRowCount = 0;
  // eslint-disable-next-line no-undef
  const rowObjStr = ssr.isOnClient ? window.localStorage.getItem('currentRow') : '';

  const sectionsByOrder = useMemo(() => {
    let filteredSectionByOrder = {};
    if (Object.keys(sectionsByOrderObj)?.length > 0) {
      filteredSectionByOrder = Object.values(sectionsByOrderObj)?.filter(data => {
        if (data?.[0]?.sectionCode === VALID_SECTION_CODES.BLACK && getMMT_HLD_OBJ()?.mmtBlack) {
          const { mmtBlackPdtData } = data?.[0]?.cards?.[0]?.mmtBlackDetail || {};
          const eVar108Value = `Holidays|listing:v3|MMT${
            mmtBlackPdtData?.mmtBlackGCAttached ? '|GC_1' : '|GC_0'
          }`;
          const trackingData = {
            effectivePriceBucket: mmtBlackPdtData?.mmtBlackBucketDetail?.effectivePriceBucket,
            gcBucket: mmtBlackPdtData?.mmtBlackBucketDetail?.gcBucket,
            myCashBucket: mmtBlackPdtData?.mmtBlackBucketDetail?.myCashBucket,
          };
          OmnitureUtils.trackLoadEvent({
            prop54: 'RENDERED_MMT_BLACK',
            prop1: data?.[0]?.order,
            eVar108: eVar108Value,
            eVar76: `${trackingData.gcBucket} | ${trackingData.myCashBucket} | ${trackingData.effectivePriceBucket}`,
          });
        }
        if (
          (data?.[0]?.sectionCode === VALID_SECTION_CODES.RECENTLY_VIEWED && !showPDO) ||
          (data?.[0]?.sectionCode === VALID_SECTION_CODES.PDO && showPDO) ||
          (data?.[0]?.sectionCode !== VALID_SECTION_CODES.PDO &&
            data?.[0]?.sectionCode !== VALID_SECTION_CODES.RECENTLY_VIEWED)
        ) {
          return data;
        }
      });
    }

    return filteredSectionByOrder;
  }, [JSON.stringify(sectionsByOrderObj)]);

  useEffect(() => {
    if (!groupMetaIsLoading && !sectionsLoading && !isLoading && activeTab?.id) {
      pageLoadingAction(false);
    }
  }, [groupMetaIsLoading, sectionsLoading, isSectionsLoading, isLoading]);

  useEffect(() => {
    removeLocalStorage('storedPackages');
    removeLocalStorage('storedSections');
    removeLocalStorage('loadedSections');
  }, []);

  useMemo(() => getAllAppliedFilters(listingFilters, search), [listingFilters, search]);

  const batchedPackages = useMemo(() => {
    let batchedPackagesList = [];

    for (let i = 0; i < packages.length; i++) {
      const currentBatchIndex = Math.floor(i / 2);
      const currentBatch = batchedPackagesList[currentBatchIndex] || [];
      currentBatch.push(packages[i]);

      batchedPackagesList[currentBatchIndex] = currentBatch;
    }

    if (isLoading) {
      if (batchedPackagesList[batchedPackagesList.length - 1]?.length === 1) {
        batchedPackagesList[batchedPackagesList.length - 1].push({ isLoaderCard: true });
      }
      batchedPackagesList = [
        ...batchedPackagesList,
        ...new Array(5).fill([{ isLoaderCard: true }, { isLoaderCard: true }]),
      ];
    }

    return batchedPackagesList;
  }, [packages, isLoading]);

  useEffect(() => {
    if (nextOffset > 0) {
      observerRef.current = new IntersectionObserver(
        async ([entry]) => {
          if (!entry.isIntersecting) return;
          if (!isLoading && !noMorePackages) {
            loadMorePackages({ offset: nextOffset, group: activeTab });
          }
        },
        { rootMargin: '50px' }
      );

      observerRef.current.observe(root.current);
    }

    return () => {
      if (root.current && observerRef.current) {
        observerRef.current.unobserve(root.current);
      }
    };
  }, [isLoading, noMorePackages, activeTab, nextOffset]);

  const activeTabIndex = useMemo(
    () => groupList?.findIndex(group => group?.id === activeTab?.id),
    [activeTab, groupList]
  );

  useEffect(() => {
    let rowObj = null;
    if (rowObjStr) {
      rowObj = JSON.parse(rowObjStr);
    }
    if (
      !groupMetaIsLoading &&
      !sectionsLoading &&
      activeTab?.id !== null &&
      rowObj?.activeTab?.id &&
      rowObj !== null &&
      (rowObj?.currentRow === 0 || rowObj?.activeTab?.id !== activeTab?.id) &&
      (!isLoading || fromSEO) &&
      loadEventTriggered &&
      !pageLoading
    ) {
      setTimeout(() => {
        OmnitureUtils.trackClickEvent({
          prop54: `Viewed_Package_${rowObj?.currentRow}`,
          prop1: `Viewed_${
            groupList?.length
              ? `Collection_${groupList?.findIndex(group => group?.id === activeTab?.id)}`
              : 'Listing Page'
          }`,
        });
      }, 1000);
    }
  }, [JSON.stringify(rowObjStr), pageLoading, loadEventTriggered]);

  const getAllSortedSections = () => {
    let currentSections = [];
    Object?.values(sectionsByOrder)?.map(sectionDataArr => {
      sectionDataArr?.map(sectionDataObj => {
        if (sectionDataObj.cards?.length > 0) {
          currentSections.push(sectionDataObj);
        }
      });
    });
    if (activeTab?.sections) {
      currentSections.push(...(activeTab?.sections || []));
    }
    if (activeTab?.groupCards) {
      activeTab.groupCards.map(groupCard => {
        currentSections.push({
          ...groupCard,
          sectionCode: CUSTOM_SECTIONS.CONTENT_CARD,
          type: CUSTOM_SECTIONS.CONTENT_CARD,
          order: groupCard?.position || 0,
        });
      });
    }
    currentSections = currentSections.sort((a, b) => a.order - b.order);
    return currentSections;
  };

  const renderSections = index => {
    const allSectionList = getAllSortedSections();
    const sectionsCount = allSectionList.length;
    const renderData = [];
    if (
      sectionsCount > 0 &&
      !sectionsLoading &&
      !isSectionsLoading &&
      // !fromSEO &&
      // eslint-disable-next-line no-undef
      ssr.isOnClient &&
      !batchedPackages[batchedPackages?.length || 0 - 1]?.[0]?.isLoaderCard &&
      !isLoading &&
      !pageLoading
    ) {
      while (usedSectionsCount < allSectionList?.length) {
        const sectionsList = allSectionList.filter(sectionData => sectionData?.order === rowCount);
        if (sectionsList.length > 0) {
          rowCount += 1;
          sectionsList.map((sectionData, sectionIndex) => {
            usedSectionsCount++;
            renderData.push(
              <Fragment key={`${sectionData?.sectionCode}_${index}_${sectionIndex}`}>
                <ScrollTracker
                  rowNumber={actualRowCount + sectionIndex}
                  isLoading={(groupMetaIsLoading && !tabs?.length) || !activeTab?.id}
                  groupId={activeTab?.id}
                  sectionsLoading={sectionsLoading}
                  sectionCode={sectionData?.sectionCode}
                  prop54={`Viewed_Section_${sectionData?.sectionCode}`}
                  prop1={`Viewed_${
                    groupList?.length ? `Collection_${activeTabIndex}` : 'Listing Page'
                  }`}
                  activeTabData={activeTab}
                  sectionData={sectionData}
                />
                <div onClick={() => faOnPageExit()}>
                  <ListingSection
                    section={sectionData}
                    rowNumber={actualRowCount + index}
                    isSectionsLoading={isSectionsLoading}
                    omnitureUtils={OmnitureUtils}
                  />
                </div>
              </Fragment>
            );
          });
          actualRowCount += sectionsList.length;
        } else {
          break;
        }
      }
    }
    return renderData;
  };

  const renderRemainingSections = batchedPackagesTotalCount => {
    const allSectionList = getAllSortedSections();

    const unrenderedSections =
      usedSectionsCount > 0 ? allSectionList.splice(usedSectionsCount) : allSectionList;

    return (
      batchedPackagesTotalCount === batchedPackages.length &&
      // !fromSEO &&
      // eslint-disable-next-line no-undef
      ssr.isOnClient &&
      !batchedPackages[batchedPackages?.length || 0 - 1]?.[0]?.isLoaderCard &&
      !isSectionsLoading &&
      sectionsLoading &&
      !isLoading &&
      !pageLoading &&
      unrenderedSections.map((sectionData, index) => (
        <Fragment key={`${sectionData?.sectionCode}_${index}`}>
          <ScrollTracker
            rowNumber={actualRowCount + index}
            isLoading={(groupMetaIsLoading && !tabs?.length) || !activeTab?.id}
            groupId={activeTab?.id}
            sectionsLoading={sectionsLoading}
            sectionCode={sectionData?.sectionCode}
            prop54={`Viewed_Section_${sectionData?.sectionCode}`}
            prop1={`Viewed_${groupList?.length ? `Collection_${activeTabIndex}` : 'Listing Page'}`}
            activeTabData={activeTab}
            sectionData={sectionData}
          />
          <div onClick={() => faOnPageExit()}>
            <ListingSection
              section={sectionData}
              rowNumber={actualRowCount + index}
              isSectionsLoading={isSectionsLoading}
              OmnitureUtils={OmnitureUtils}
            />
          </div>
        </Fragment>
      ))
    );
  };

  const renderPackage = (packageList, index) => {
    rowCount++;
    actualRowCount++;
    return packageList.map((packageDetails, j) =>
      packageDetails?.isLoaderCard ? (
        <PackageCardLoading key={j} />
      ) : (
        <Hydrator
          component={PackageCard}
          packageDetails={packageDetails}
          search={search}
          key={j}
          packageIndex={index * 2 + j}
          activeTabIndex={activeTabIndex}
          fallback={<PackageCardLoading />}
        />
      )
    );
  };

  return (
    <div className="packageDetails">
      <div className="topDetailsSection">
        <div className="headingSection">
          <AppliedFilters
            listingFilters={listingFilters}
            handleFilterChange={handleFilterChange}
            handleClearAllFilters={handleClearAllFilters}
          />
          <ListingSorter packageCount={activeTab?.totalPackageCount || packagesCount} />
        </div>
      </div>

      {/* {fromSEO && <AboutPackages />} */}
      {!isLoading && batchedPackages?.length === 0 ? (
        <ErrorPage
          type={ERROR_TYPES.NO_PACKAGES}
          listingFilters={listingFilters}
          activeTabIndex={activeTabIndex}
          title={noPackageError?.header}
          subTitle={noPackageError?.subHeader}
        />
      ) : (
        <>
          {batchedPackages.map((packageList, i) => {
            batchedPackagesTotalCount = i + 1;
            return (
              <Fragment key={i}>
                {renderSections(i)}
                {!batchedPackages[batchedPackages?.length || 0 - 1]?.[0]?.isLoaderCard &&
                  (!isLoading || fromSEO) &&
                  !sectionsLoading &&
                  activeTab?.id &&
                  !groupMetaIsLoading && (
                    <ScrollTracker
                      rowNumber={actualRowCount}
                      isLoading={(groupMetaIsLoading && !tabs?.length) || !activeTab?.id}
                      groupId={activeTab?.id}
                      sectionsLoading={sectionsLoading}
                      fireEvent={false}
                      activeTabData={activeTab}
                      prop54="Viewed_Package"
                      prop1={`Viewed_${
                        groupList?.length ? `Collection_${activeTabIndex}` : 'Listing Page'
                      }`}
                      packageList={packageList}
                    />
                  )}
                <div className="packageCardList">{renderPackage(packageList, i)}</div>
              </Fragment>
            );
          })}
          {renderRemainingSections(batchedPackagesTotalCount)}
        </>
      )}
      <div ref={root} />
    </div>
  );
};

const mapStateToProps = (
  { listingPackages, groupMetadata, metadata, sections, subMenu },
  props
) => ({
  packages: listingPackages?.packagesByGroupId?.[props?.activeTab?.id]?.packages,
  packagesCount: listingPackages?.packagesByGroupId?.[props?.activeTab?.id]?.numFound,
  noMorePackages:
    listingPackages?.packagesByGroupId?.[props?.activeTab?.id]?.noMorePackages ?? true,
  isLoading: listingPackages?.isLoading?.[props?.activeTab?.id] ?? true,
  nextOffset: listingPackages?.packagesByGroupId?.[props?.activeTab?.id]?.nextOffset ?? 0,
  groupMetaIsLoading: groupMetadata?.isLoading,
  listingFilters: metadata?.listingFilters || [],
  sectionsByOrderObj: sections?.sectionsByOrder || {},
  metadataIsLoading: metadata?.isLoading || false,
  listingSorters: metadata?.listingSorters,
  groupList: groupMetadata?.groupDetailsList || [],
  sectionsLoading: sections?.isLoading || false,
  isSectionsLoading: sections?.isSectionsLoading || false,
  pageLoading: listingPackages?.pageLoading || false,
  subMenu: subMenu?.isLoading || false,
  noPackageError: listingPackages?.packagesByGroupId?.[props?.activeTab?.id]?.errorMessage,
});

export default connect(mapStateToProps, null)(PackageDetails);
